import { sortBy } from "lodash";
import {
  InstrumentStatus,
  RiskToleranceQuestionnaireAnswer,
  RiskToleranceQuestionnaireField
} from "@/shared/types";
type TextOf<T extends string> = { [K in T]: string };

export const statusMap: Record<InstrumentStatus, string> = {
  none: "None",
  active: "Active",
  inactive: "Inactive",
  closeOnly: "Close Only",
  halted: "Halted",
  unavailableToPurchase: "Unavailable To Purchase",
};

const getKeys = <T extends string>(textOf: TextOf<T>) => {
  return Object.keys(textOf) as T[];
};
interface SelectOptionData<T extends string> {
  keys: T[];
  getValue: (key: T) => string;
  options: { text: string, value: T}[];
}

const getGetValue = <T extends string>(textOf: TextOf<T>) => (key: T) => textOf[key] || "";

const factoryFactory = <T extends string>(textOf: TextOf<T>, sort = false) => (): SelectOptionData<T> => {
  const keys = sort ? sortBy(getKeys(textOf), k => textOf[k]) : getKeys(textOf);
  return {
    keys,
    getValue: getGetValue(textOf),
    options: keys.map(k => ({text: textOf[k], value: k}))
  };
};

export type EmploymentStatus =
  "EMPLOYED" |
  "RETIRED" |
  "STUDENT" |
  "UNEMPLOYED"|
  "SELF_EMPLOYED";

const employmentStatusText: TextOf<EmploymentStatus> = {
  EMPLOYED: "Employed (full-time or part-time)",
  RETIRED: "Retired",
  STUDENT: "Student",
  UNEMPLOYED: "Unemployed",
  SELF_EMPLOYED: "Self-employed"
};

export const employmentStatusFactory = factoryFactory(employmentStatusText);

const rtmAnswerText: TextOf<RiskToleranceQuestionnaireAnswer> = {
  stronglyDisagree: "Strongly Disagree",
  disagree: "Disagree",
  neutral: "Neutral",
  agree: "Agree",
  stronglyAgree: "Strongly Agree"
};
export const RtqQuestionaireFactory = factoryFactory(rtmAnswerText);

// questions into array here, iterate over them in the view

export const RiskToleranceQuestionnaireFields: RiskToleranceQuestionnaireField[] = [
  {
    id: "1",
    label: "I am comfortable with the idea of investing a portion of my Health Savings Account in order to potentially earn higher returns.",
    userResponse: null
  },
  {
    id: "2",
    label: "I am willing to accept some fluctuations in the value of my Health Savings Account if it means the potential for higher long-term growth.",
    userResponse: null
  },
  {
    id: "3",
    label: "In the event of a market downturn affecting my investments, I would prefer to stay invested and wait for the market to recover, rather than sell investments immediately.",
    userResponse: null
  },
  {
    id: "4",
    label: "I have a good understanding of different investment options that I can select, and I am willing to research and educate myself further to make informed decisions about my investments.",
    userResponse: null
  },
  {
    id: "5",
    label: "My investment horizon for my Health Savings Account is at least 5 years or more.",
    userResponse: null
  },
];

export const ManagedRiskToleranceQuestionnaireFields: RiskToleranceQuestionnaireField[] = [
  {
    id: "1",
    label: "I am comfortable with the idea of investing a portion of my Health Savings Account in order to potentially earn higher returns.",
    userResponse: null
  },
  {
    id: "2",
    label: "I am willing to accept some fluctuations in the value of my Health Savings Account if it means the potential for higher long-term growth.",
    userResponse: null
  },
  {
    id: "3",
    label: "In the event of a market downturn affecting my Health Savings Account investments, I would prefer to stay invested and wait for the market to recover, rather than sell investments immediately.",
    userResponse: null
  },
  {
    id: "4",
    label: "Preserving the value of my Health Savings Account is more important to me than the potential for higher returns.",
    userResponse: null
  },
  {
    id: "5",
    label: "I have a long investment horizon and do not need immediate access to the funds in my HSA.",
    userResponse: null
  },
  {
    id: "6",
    label: "I am open to diversifying my HSA investments across different asset classes (e.g., stocks, bonds, real estate) with the oversight of a professional investment manager.",
    userResponse: null
  },
  {
    id: "7",
    label: "I am comfortable with a higher degree of complexity in my investment strategy if it has the potential for higher returns and is overseen by a professional investment manager.",
    userResponse: null
  },
  {
    id: "8",
    label: "I believe that staying invested in the market over the long term is generally a better strategy than trying to time the market.",
    userResponse: null
  },
  {
    id: "9",
    label: "I am prepared to accept the possibility that my HSA investments may underperform for a period of time.",
    userResponse: null
  },
  {
    id: "10",
    label: "I am comfortable with investing in assets that have historically shown higher volatility but also the potential for higher returns.",
    userResponse: null
  },
];

export type JobSector =
  "AGRICULTURE" |
  "MINING" |
  "UTILITIES" |
  "CONSTRUCTION" |
  "MANUFACTURING" |
  "WHOLESALE" |
  "RETAIL" |
  "TRANSPORT" |
  "INFORMATION" |
  "FINANCE" |
  "REAL_ESTATE" |
  "PROFESSIONAL" |
  "MANAGEMENT" |
  "EDUCATION" |
  "HEALTH" |
  "ART" |
  "FOOD" |
  "PUBLIC" |
  "WASTE";

const jobSectorText: TextOf<JobSector> = {
  AGRICULTURE: "Agriculture, Forestry, Fishing and Hunting",
  MINING: "Mining, Quarrying, and Oil and Gas Extraction",
  UTILITIES: "Utilities",
  CONSTRUCTION: "Construction",
  MANUFACTURING: "Manufacturing",
  WHOLESALE: "Wholesale Trade",
  RETAIL: "Retail Trade",
  TRANSPORT: "Transportation and Warehousing",
  INFORMATION: "Information",
  FINANCE: "Finance and Insurance",
  REAL_ESTATE: "Real Estate and Rental and Leasing",
  PROFESSIONAL: "Professional, Scientific, and Technical Services",
  MANAGEMENT: "Management of Companies and Enterprises",
  EDUCATION: "Educational Services",
  HEALTH: "Health Care and Social Assistance",
  ART: "Arts, Entertainment, and Recreation",
  FOOD: "Accommodation and Food Services",
  PUBLIC: "Public Administration",
  WASTE: "Administrative and Support and Waste Management and Remediation Services",
};

export const jobSectorFactory = factoryFactory(jobSectorText, true);

export type Occupation =
  "ACCOUNTANT" |
  "ACTUARY" |
  "ADJUSTER" |
  "ADMINISTRATOR" |
  "ADVERTISER" |
  "AGENT" |
  "ATC" |
  "AMBASSADOR" |
  "ANALYST" |
  "APPRAISER" |
  "ARCHITECT" |
  "ARTIST" |
  "ASSISTANT" |
  "ATHLETE" |
  "ATTENDANT" |
  "ATTORNEY" |
  "AUCTIONEER" |
  "AUDITOR" |
  "BARBER" |
  "BROKER" |
  "BUSINESS_EXEC" |
  "BUSINESS_OWNER" |
  "CAREGIVER" |
  "CARPENTER" |
  "CASHIER" |
  "CHEF" |
  "CHIROPRACTOR" |
  "CIVIL" |
  "CLERGY" |
  "CLERK" |
  "COMPLIANCE" |
  "CONSULTANT" |
  "CONTRACTOR" |
  "COUNSELOR" |
  "CUSTOMER_SERVICE" |
  "DEALER" |
  "DEVELOPER" |
  "DISTRIBUTOR" |
  "DOCTOR" |
  "DRIVER" |
  "ENGINEER" |
  "EXAMINER" |
  "EXTERMINATOR" |
  "FACTORY" |
  "FARMER" |
  "FINANCIAL" |
  "FISHERMAN" |
  "FLIGHT" |
  "HR" |
  "IMPEX" |
  "INSPECTOR" |
  "INTERN" |
  "INVESTMENT" |
  "INVESTOR" |
  "IT" |
  "JANITOR" |
  "JEWELER" |
  "LABORER" |
  "LANDSCAPER" |
  "LENDING" |
  "MANAGER" |
  "MECHANIC" |
  "MILITARY" |
  "MORTICIAN" |
  "NURSE" |
  "NUTRITIONIST" |
  "OFFICE" |
  "PHARMACIST" |
  "PHYSICAL" |
  "PILOT" |
  "POLICE" |
  "POLITICIAN" |
  "PM" |
  "REP" |
  "RESEARCHER" |
  "SAILOR" |
  "SALES" |
  "SCIENTIST" |
  "SEAMSTRESS" |
  "SECURITY" |
  "SOCIAL" |
  "TEACHER" |
  "TECHNICIAN" |
  "TELLER" |
  "TRAINER" |
  "TRANSPORTER" |
  "UNDERWRITER" |
  "WRITER";

const occupationText: TextOf<Occupation> = {
  ACCOUNTANT: "Accountant/CPA/Bookkeeper/Controller",
  ACTUARY: "Actuary",
  ADJUSTER: "Adjuster",
  ADMINISTRATOR: "Administrator",
  ADVERTISER: "Advertiser/Marketer/PR Professional",
  AGENT: "Agent",
  ATC: "Air Traffic Controller",
  AMBASSADOR: "Ambassador/Consulate Professional",
  ANALYST: "Analyst",
  APPRAISER: "Appraiser",
  ARCHITECT: "Architect/Designer",
  ARTIST: "Artist/Performer/Actor/Dancer",
  ASSISTANT: "Assistant",
  ATHLETE: "Athlete",
  ATTENDANT: "Attendant",
  ATTORNEY: "Attorney/Judge/Legal Professional",
  AUCTIONEER: "Auctioneer",
  AUDITOR: "Auditor",
  BARBER: "Barber/Beautician/Hairstylist",
  BROKER: "Broker",
  BUSINESS_EXEC: "Business Executive (VP, Director, etc.)",
  BUSINESS_OWNER: "Business Owner",
  CAREGIVER: "Caregiver",
  CARPENTER: "Carpenter/Construction Worker",
  CASHIER: "Cashier",
  CHEF: "Chef/Cook",
  CHIROPRACTOR: "Chiropractor",
  CIVIL: "Civil Servant",
  CLERGY: "Clergy",
  CLERK: "Clerk",
  COMPLIANCE: "Compliance/Regulatory Professional",
  CONSULTANT: "Consultant",
  CONTRACTOR: "Contractor",
  COUNSELOR: "Counselor/Therapist",
  CUSTOMER_SERVICE: "Customer Service Representative",
  DEALER: "Dealer",
  DEVELOPER: "Developer",
  DISTRIBUTOR: "Distributor",
  DOCTOR: "Doctor/Dentist/Veterinarian/Surgeon",
  DRIVER: "Driver",
  ENGINEER: "Engineer",
  EXAMINER: "Examiner",
  EXTERMINATOR: "Exterminator",
  FACTORY: "Factory/Warehouse Worker",
  FARMER: "Farmer/Rancher",
  FINANCIAL: "Financial Planner",
  FISHERMAN: "Fisherman",
  FLIGHT: "Flight Attendant",
  HR: "Human Resources Professional",
  IMPEX: "Importer/Exporter",
  INSPECTOR: "Inspector/Investigator",
  INTERN: "Intern",
  INVESTMENT: "Investment Advisor/Investment Manager",
  INVESTOR: "Investor",
  IT: "IT Professional/IT Associate",
  JANITOR: "Janitor",
  JEWELER: "Jeweler",
  LABORER: "Laborer",
  LANDSCAPER: "Landscaper",
  LENDING: "Lending Professional",
  MANAGER: "Manager",
  MECHANIC: "Mechanic",
  MILITARY: "Military, Officer or Associated",
  MORTICIAN: "Mortician/Funeral Director",
  NURSE: "Nurse",
  NUTRITIONIST: "Nutritionist",
  OFFICE: "Office Associate",
  PHARMACIST: "Pharmacist",
  PHYSICAL: "Physical Therapist",
  PILOT: "Pilot",
  POLICE: "Police Officer/Firefighter/Law Enforcement Professional",
  POLITICIAN: "Politician",
  PM: "Project Manager",
  REP: "Registered Rep",
  RESEARCHER: "Researcher",
  SAILOR: "Sailor/Seaman",
  SALES: "Salesperson",
  SCIENTIST: "Scientist",
  SEAMSTRESS: "Seamstress/Tailor",
  SECURITY: "Security Guard",
  SOCIAL: "Social Worker",
  TEACHER: "Teacher/Professor",
  TECHNICIAN: "Technician",
  TELLER: "Teller",
  TRAINER: "Trainer/Instructor",
  TRANSPORTER: "Transporter",
  UNDERWRITER: "Underwriter",
  WRITER: "Writer/Journalist/Editor"
};

export const occupationFactory = factoryFactory(occupationText, true);

export type InvestmentObjective =
  "LONG_TERM" |
  "INFREQUENT" |
  "FREQUENT" |
  "ACTIVE_DAILY" |
  "NEW";

export type InvestmentExperience =
  "NONE" |
  "YRS_1_2" |
  "YRS_3_5" |
  "YRS_5_10" |
  "YRS_10_";

export type RiskTolerance =
  "LOW" |
  "MODERATE" |
  "SPECULATION" |
  "HIGH";

export type AccountType =
  "ADVISORY" |
  "SELF" |
  "RIA_MANAGED" |
  "NONE";

export const InvestmentSignatureAttrName = "investmentaccounttype";

export let advisoryDocumentKeyMap: Record<string, string> = {
  // maps to CURATED
  advisory: "abg_advisory_curated",
  // maps  to SELF-DIRECTED
  openBrokerage: "abg_advisory_selfdirected",
  // maps to MANAGED
  riaManaged: "abg_advisory_managed"
};
